// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The evidence tile's 360° view: the panoramas near the part
 * (`GET /instances/{cloud}/{id}/panoramas`), the nearest resected one opened
 * as a pannable strip centred on the part (`PanoStrip`, spec A5), others a
 * click away, and "Åbn i Model" into the Ground's immersive 360°. An editor
 * makes the shown panorama the part's resource image ("Brug som
 * ressourcebillede": the ~60° window round the part, `panoView`); the chosen
 * one says "Valgt ✓" (frontend-r22.1). "Del op i 360°" opens SAM3 on the
 * shown panorama under the strip (`PanoSegment`: text prompts, confidence,
 * equator tiles, mask → resource).
 */

import { useEffect, useState } from 'react';

import { api } from '../../../api/client';
import type { ResourceView, SegmentResourceResult } from '../../../api/types';
import { useAsync } from '../../../app/useAsync';
import { GENERIC_IMAGE_ERROR } from '../../../kortlaegning/evidence';
import { PANO_STRIP_MAX_SIZE, panoCaption, resolvePano, type PanoLookup } from '../../../kortlaegning/pano';
import { instanceKey } from '../../../kortlaegning/photo';
import { panoChoiceState, panoView } from '../../../kortlaegning/view';
import { formatNumber } from '../../../kortlaegning/vocab';
import { Button } from '../../Button';
import { PanoStrip } from '../../kortlaegning/PanoStrip';
import { PanoSegment } from './PanoSegment';
import styles from './PanoView.module.css';

export interface PanoViewProps {
  cloud: string;
  instanceId: number;
  roomName: string | null;
  /** "Åbn i Model": the Ground's 360° at this panorama. */
  onOpenModel: (panoramaId: number) => void;
  /** The part's current resource image, to mark the chosen panorama. */
  view: ResourceView | null;
  /** "Brug som ressourcebillede"; absent = read-only. */
  onChooseView?: (view: ResourceView) => void;
  /** A part was filed from the panorama's mask. */
  onCreated?: (result: SegmentResourceResult) => void;
}

/** Panoramas offered as alternatives (nearest first). */
const ALTERNATIVES = 6;

export function PanoView({ cloud, instanceId, roomName, onOpenModel, view, onChooseView, onCreated }: PanoViewProps) {
  const key = instanceKey(cloud, instanceId);
  const lookup = useAsync<PanoLookup>(
    async (signal) => {
      try {
        const result = await api.instancePanoramas(cloud, instanceId, signal);
        return { key, panoramas: result.panoramas, failed: false };
      } catch (cause) {
        if (signal.aborted) throw cause;
        return { key, panoramas: [], failed: true };
      }
    },
    [cloud, instanceId],
  );
  const { pano: picked, empty } = resolvePano(key, lookup.data);
  const list = lookup.data?.key === key ? lookup.data.panoramas : [];

  // The user's choice among the panoramas; the pick (nearest resected) until then.
  const [chosen, setChosen] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [segmenting, setSegmenting] = useState(false);
  useEffect(() => setChosen(null), [key]);
  const pano = list.find((p) => p.panorama_id === chosen) ?? picked;
  useEffect(() => setFailed(false), [pano?.panorama_id]);

  if (!pano) {
    return (
      <div className={styles.view}>
        <div className={styles.media}>
          <p className={styles.empty}>{empty}</p>
        </div>
      </div>
    );
  }

  const others = list.slice(0, ALTERNATIVES);
  const choice = panoChoiceState(view, pano.panorama_id);
  return (
    <div className={styles.view}>
      <div className={styles.media}>
        {failed ? (
          <p className={styles.empty}>{GENERIC_IMAGE_ERROR}</p>
        ) : (
          <PanoStrip
            className={styles.strip}
            url={api.panoramaImageUrl(pano.panorama_id, { maxSize: PANO_STRIP_MAX_SIZE })}
            u={pano.u}
            v={pano.v}
            marker={pano.heading === 'resected'}
            label={`360°-optagelse ${pano.panorama_id}${roomName ? ` · ${roomName}` : ''}`}
            onError={() => setFailed(true)}
          />
        )}
      </div>
      <div className={styles.foot}>
        <span className={styles.muted}>
          {panoCaption(roomName, pano.heading)} · P-{pano.panorama_id} · {formatNumber(pano.distance)} m fra delen · træk for at se dig
          omkring
        </span>
        {choice.chosen ? (
          <span className={`${styles.chosen} ${styles.end}`}>{choice.text}</span>
        ) : (
          onChooseView && (
            <Button
              className={styles.end}
              onClick={() => onChooseView(panoView(pano.panorama_id, pano.u, pano.v, pano.heading === 'resected'))}
              title={pano.heading === 'levelled' ? 'Retningen er ukendt — hele optagelsen gemmes' : undefined}
            >
              {choice.text}
            </Button>
          )
        )}
        <Button
          className={choice.chosen || onChooseView ? undefined : styles.end}
          aria-expanded={segmenting}
          onClick={() => setSegmenting((v) => !v)}
        >
          {segmenting ? 'Skjul Del op' : 'Del op i 360°'}
        </Button>
        <Button variant="primary" onClick={() => onOpenModel(pano.panorama_id)}>
          Åbn i Model
        </Button>
      </div>
      {segmenting && (
        <div className={styles.segment}>
          <PanoSegment panoramaId={pano.panorama_id} onCreated={onCreated} />
        </div>
      )}
      {others.length > 1 && (
        <div className={styles.choices} role="group" aria-label="Andre 360°-optagelser">
          {others.map((p) => (
            <button
              key={p.panorama_id}
              type="button"
              className={styles.choice}
              aria-pressed={p.panorama_id === pano.panorama_id}
              onClick={() => setChosen(p.panorama_id)}
              title={p.heading === 'levelled' ? 'Retningen er ukendt — ingen markør' : undefined}
            >
              P-{p.panorama_id} · {formatNumber(p.distance)} m{p.heading === 'levelled' ? ' · retning ukendt' : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
